import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Upload, Play, Clock, CheckCircle2, AlertTriangle, XCircle, Loader2,
    ExternalLink, Download, Sparkles, History,
} from "lucide-react";
import VideoCostModal from "./VideoCostModal";
import RegenerateWithNotesModal from "./RegenerateWithNotesModal";
import VersionCompareModal from "./VersionCompareModal";

/**
 * VideoShotCard — V1-A.
 *
 * Renders a single storyboard shot with:
 *   - read-only video_prompt preview (editable via toggle)
 *   - reference image picker (V1-B+; visible but locked in V1-A)
 *   - manual import button (V1-A path — uploads via /api/uploads then
 *     submits a manual_import video_job)
 *   - Generate (V1-B placeholder, disabled in V1-A)
 *   - live status chip + output preview + approve-to-QC
 *
 * Props:
 *   project: project doc
 *   shot:    { shot_id, video_prompt, image_prompt, ... }
 *   jobs:    array of video_jobs for this shot (sorted desc by submitted_at)
 *   onChange: () => void   // parent reload trigger
 */
export default function VideoShotCard({ project, shot, jobs, onChange, providers }) {
    const [editing, setEditing] = useState(false);
    const [prompt, setPrompt] = useState(shot?.video_prompt || "");
    const [busy, setBusy] = useState(false);
    const [costOpen, setCostOpen] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);
    const [pendingAssetId, setPendingAssetId] = useState("");
    const fileInputRef = useRef(null);

    // iter60 — Sora controls. Default to sora-2 / 4s / 1280x720 ("cheapest"
    // option) so an accidental click is the lowest possible burn.
    // iter61 — When the shot ships a Sora prompt pack with a recommended
    // duration_seconds / size, prefer those as defaults (still
    // overridable). Otherwise fall back to the global beta defaults
    // (8s portrait — Furmais bias for short-form).
    // iter63 — Two distinct Sora tiers. The standard tier is the only
    // tier ever auto-bound to the Generate button; the premium tier is
    // surfaced only when the admin has explicitly whitelisted sora-2-pro
    // AND only as a manual retry option, never as auto-fallback.
    const soraStdCaps = providers?.capabilities?.["sora-2"] || null;
    const soraProCaps = providers?.capabilities?.["sora-2-pro"] || null;
    const soraCaps = soraStdCaps || soraProCaps || null;   // first-tier metadata used for size/dur dropdowns
    const soraEnabled = Boolean(soraStdCaps);              // Generate button gated on standard tier
    const soraProEnabled = Boolean(soraProCaps);           // Premium retry button gated on its own whitelist
    const ffmpegReady = providers?.ffmpeg_ready !== false;

    // Sprint F2 — fal.ai / Kling image-to-video button. Gated on TWO
    // signals so a stray env flip cannot expose the button without
    // backend readiness: (1) BE registered `fal-kling` in the
    // /video-studio/providers response, AND (2) the preview
    // `REACT_APP_ENABLE_FAL_KLING=true` feature flag is on. Live
    // fal.ai key stays backend-only — the button just calls
    // /api/projects/{id}/video-jobs with provider=fal-kling.
    const falKlingCaps = providers?.capabilities?.["fal-kling"] || null;
    const falKlingFlag = String(process.env.REACT_APP_ENABLE_FAL_KLING || "").toLowerCase() === "true";
    const falKlingEnabled = Boolean(falKlingCaps) && falKlingFlag;
    const [falKlingOpen, setFalKlingOpen] = useState(false);
    const [falKlingDuration, setFalKlingDuration] = useState(5);
    const falKlingCost = Number(
        (falKlingCaps?.est_usd_per_clip_by_duration || {})[String(falKlingDuration)] || 0.35,
    );

    const packDur = shot?.sora?.duration_seconds;
    const packSize = shot?.sora?.size;
    const initialDuration = (
        soraCaps && soraCaps.durations && soraCaps.durations.includes(Number(packDur))
            ? Number(packDur)
            : (soraCaps?.default_duration ?? 8)
    );
    const initialSize = (
        soraCaps && soraCaps.sizes && soraCaps.sizes.includes(String(packSize))
            ? String(packSize)
            : (soraCaps?.default_size ?? "1280x720")
    );
    // iter63 — `soraModel` is bound 1:1 to the active button. The
    // standard Generate path always sends `sora-2` (matches provider).
    // The premium retry path always sends `sora-2-pro` (matches its
    // provider entry). The Model dropdown is now read-only metadata.
    const [soraModel, setSoraModel] = useState("sora-2");
    const [soraSize, setSoraSize] = useState(initialSize);
    const [soraDuration, setSoraDuration] = useState(initialDuration);
    const [soraOpen, setSoraOpen] = useState(false);
    const [soraProOpen, setSoraProOpen] = useState(false);
    // iter65 — Director-grade regeneration modals.
    const [regenOpen, setRegenOpen] = useState(false);
    const [regenBusy, setRegenBusy] = useState(false);
    const [compareOpen, setCompareOpen] = useState(false);
    // iter66.1 — ephemeral QC routing chip. Captures the qc_review
    // payload returned by VersionCompareModal's approve action so the
    // operator sees "QC → v2" flip live before the parent reload
    // re-hydrates the canonical board. Reset whenever the underlying
    // shot prop changes (parent moved to a different shot).
    const [qcChip, setQcChip] = useState(null);
    // iter62 — Track operator intent so a refresh of capabilities does
    // not blow away a manual selection. If the user changes any Sora
    // control we set `userTouchedSoraControls=true`; the capability
    // hydration effect below only re-initialises while this is false.
    const [userTouchedSoraControls, setUserTouchedSoraControls] = useState(false);

    useEffect(() => { setPrompt(shot?.video_prompt || ""); }, [shot?.video_prompt]);

    // iter62 — When provider capabilities arrive async (after mount),
    // re-derive the Sora controls from the freshly-loaded caps + the
    // shot's pack hint. Without this, a card that mounted before
    // /video-studio/providers responded was stuck on the hardcoded
    // fallback (8s, 1280x720) regardless of pack defaults.
    useEffect(() => {
        if (!soraCaps) return;
        if (userTouchedSoraControls) return;
        const pdur = shot?.sora?.duration_seconds;
        const psize = shot?.sora?.size;
        const dur = (
            soraCaps.durations && soraCaps.durations.includes(Number(pdur))
                ? Number(pdur)
                : (soraCaps.default_duration ?? 8)
        );
        const size = (
            soraCaps.sizes && soraCaps.sizes.includes(String(psize))
                ? String(psize)
                : (soraCaps.default_size ?? "1280x720")
        );
        setSoraDuration(dur);
        setSoraSize(size);
        // iter63 — Model is always pinned to "sora-2" by the standard
        // button. We never auto-flip to sora-2-pro even if both tiers
        // are enabled — premium is a manual retry.
        setSoraModel("sora-2");
    }, [
        soraCaps?.default_size,
        soraCaps?.default_duration,
        // re-key on length so adding/removing supported durations
        // also re-runs (e.g. SDK upgrade unlocks 720x1280).
        (soraCaps?.durations || []).length,
        (soraCaps?.sizes || []).length,
        shot?.sora?.duration_seconds,
        shot?.sora?.size,
        userTouchedSoraControls,
    ]);

    const active = (jobs || []).find(j =>
        ["queued", "submitted", "processing"].includes(j.status));
    const succeeded = (jobs || []).find(j => j.status === "succeeded");

    const pickFile = () => fileInputRef.current?.click();

    const onFileSelected = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!/^video\/(mp4|quicktime|webm)$/.test(f.type)) {
            toast.error("Please pick an MP4 / MOV / WebM file.");
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            fd.append("asset_type", "video");
            fd.append("usage_tags", "generated_video");
            const { data } = await api.post("/uploads", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 240000,
            });
            const assetId = data?.asset?.id || data?.id || "";
            if (!assetId) throw new Error("Upload returned no asset id");
            setPendingAssetId(assetId);
            setPendingFile(f);
            setCostOpen(true);
        } catch (err) {
            toast.error(err?.userMessage || "Upload failed");
        } finally { setBusy(false); }
    };

    const submitManualImport = async () => {
        if (!pendingAssetId) return;
        setBusy(true);
        try {
            await api.post(`/projects/${project.id}/video-jobs`, {
                shot_id: shot.shot_id,
                provider: "manual_import",
                duration_seconds: 4,
                size: "1280x720",
                manual_import_asset_id: pendingAssetId,
                video_prompt_override: prompt,
            });
            toast.success("Imported and ready for QC.");
            setCostOpen(false);
            setPendingFile(null);
            setPendingAssetId("");
            onChange?.();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail?.error === "duplicate_active_job") {
                toast.error("A job for this shot is already in progress.");
            } else if (detail?.error === "daily_cap_reached") {
                toast.error(`Daily cap reached (${detail.cap}). Try again tomorrow.`);
            } else {
                toast.error(err?.userMessage || "Submit failed");
            }
            setCostOpen(false);
        } finally { setBusy(false); }
    };

    const cancel = async (jobId) => {
        try {
            await api.post(`/projects/${project.id}/video-jobs/${jobId}/cancel`);
            toast.success("Cancelled");
            onChange?.();
        } catch (err) { toast.error(err?.userMessage || "Cancel failed"); }
    };

    // Sprint F2 — fal.ai / Kling image-to-video submission. Reuses the
    // existing paid-cost gate (confirmed=true) and daily caps. The FE
    // does NOT send a base64 image — the BE resolves the reference
    // through the G1 resolver + staging pipeline server-side.
    const submitFalKling = async () => {
        if (!falKlingEnabled) return;
        if (!prompt.trim()) {
            toast.error("Add a video_prompt before generating.");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/projects/${project.id}/video-jobs`, {
                shot_id: shot.shot_id,
                provider: "fal-kling",
                model: "fal-kling",
                duration_seconds: Number(falKlingDuration) || 5,
                size: falKlingCaps?.default_size || "1280x720",
                video_prompt_override: prompt,
                confirmed: true,
            });
            toast.success("Kling job queued. Kling v2.5 turbo pro usually completes in 2–4 minutes.");
            setFalKlingOpen(false);
            onChange?.();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail?.error === "reference_required"
                || (typeof detail === "string" && detail.startsWith("reference_required"))) {
                toast.error(
                    "fal-kling is image-to-video — please bind an authorized "
                    + "reference image (packshot / storyboard frame / character asset) "
                    + "to this shot before generating.",
                );
            } else if (detail?.error === "duplicate_active_job"
                       || detail?.error === "duplicate_active_paid_job_for_shot") {
                toast.error(detail?.hint || "A paid job for this shot is already running.");
            } else if (detail?.error === "daily_cap_reached") {
                toast.error(`Daily cap reached (${detail.cap}). Try again tomorrow.`);
            } else if (detail?.error === "daily_cost_cap_reached") {
                toast.error(
                    `Daily cost cap reached: $${Number(detail.used_usd || 0).toFixed(2)} / `
                    + `$${Number(detail.cap_usd || 0).toFixed(2)}. Wait 24h or ask admin to raise it.`,
                );
            } else if (detail?.error === "cost_modal_confirmation_required") {
                toast.error("Cost modal confirmation missing. Reopen and re-confirm.");
            } else if (typeof detail === "string" && detail.startsWith("provider_not_allowed")) {
                toast.error("fal-kling is not enabled on this server. Ask admin to whitelist it.");
            } else if (typeof detail === "string" && detail.startsWith("invalid_")) {
                toast.error(`Kling rejected this configuration: ${detail}.`);
            } else {
                toast.error(err?.userMessage || "Generate failed");
            }
            setFalKlingOpen(false);
        } finally {
            setBusy(false);
        }
    };

    // iter63 — Standard Sora-2 submission. Always sends `model="sora-2"`.
    // Never auto-fallback to premium. Single click per shot. NO bulk action.
    const submitSora = async () => {
        if (!soraEnabled) return;
        if (!prompt.trim()) {
            toast.error("Add a video_prompt before generating.");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/projects/${project.id}/video-jobs`, {
                shot_id: shot.shot_id,
                provider: "sora-2",
                model: "sora-2",                // iter63 — hard-pinned, no upgrade
                duration_seconds: Number(soraDuration) || 4,
                size: soraSize,
                video_prompt_override: prompt,
                // iter66.2 — BE requires `confirmed=true` for paid Sora.
                // The VideoCostModal calls submitSora() only after the
                // user clicked Confirm, so this is safe to send here.
                confirmed: true,
            });
            toast.success("Sora job queued. This usually takes 2–5 minutes.");
            setSoraOpen(false);
            onChange?.();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail === "video_studio_globally_disabled") {
                toast.error("Video Studio is globally disabled. Contact admin.");
            } else if (detail?.error === "duplicate_active_job"
                       || detail?.error === "duplicate_active_paid_job_for_shot") {
                toast.error(detail?.hint || "A paid Sora job for this shot is already running.");
            } else if (detail?.error === "ffmpeg_unavailable") {
                toast.error("FFmpeg missing on backend — Sora generation blocked. Ask admin to install ffmpeg.");
            } else if (detail?.error === "daily_cap_reached") {
                toast.error(`Daily cap reached (${detail.cap}). Try again tomorrow.`);
            } else if (detail?.error === "daily_cost_cap_reached") {
                toast.error(
                    `Daily Sora cost cap reached: $${Number(detail.used_usd || 0).toFixed(2)} / `
                    + `$${Number(detail.cap_usd || 0).toFixed(2)}. Try shorter clip or wait 24h.`,
                );
            } else if (detail?.error === "model_provider_mismatch") {
                toast.error(
                    "Provider/model mismatch — the standard button always sends "
                    + "model=sora-2. Premium retry uses the dedicated button.",
                );
            } else if (detail?.error === "model_not_enabled") {
                toast.error(
                    "Sora-2-Pro is not enabled on this server. Ask admin to "
                    + "whitelist it before submitting premium retries.",
                );
            } else if (detail?.error === "cost_modal_confirmation_required") {
                // iter66.2 — Defensive: should never hit this from the UI
                // because the cost modal sends `confirmed=true`. If it
                // does (e.g. stale modal state), guide the operator back.
                toast.error(
                    "Cost modal confirmation missing. Reopen the cost "
                    + "modal and click Confirm to send confirmed=true.",
                );
            } else if (typeof detail === "string" && detail.startsWith("provider_not_allowed")) {
                toast.error("Sora is not enabled on this server. Ask admin to whitelist it.");
            } else if (typeof detail === "string" && detail.startsWith("invalid_")) {
                toast.error(`Sora rejected this configuration: ${detail}.`);
            } else if (err?.response?.status === 402) {
                toast.error("Universal Key budget exhausted. Top up at Profile → Universal Key.");
            } else {
                toast.error(err?.userMessage || "Generate failed");
            }
            setSoraOpen(false);
        } finally { setBusy(false); }
    };

    // iter63 — Premium Sora-2-Pro retry. Surfaces ONLY when the admin
    // has whitelisted sora-2-pro AND the operator clicks the dedicated
    // "Retry with Sora-2-Pro (premium)" button. Never auto-triggered.
    // Cost confirmation is mandatory (cost modal shown before this fn runs).
    const submitSoraPro = async () => {
        if (!soraProEnabled) return;
        if (!prompt.trim()) {
            toast.error("Add a video_prompt before generating.");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/projects/${project.id}/video-jobs`, {
                shot_id: shot.shot_id,
                provider: "sora-2-pro",
                model: "sora-2-pro",            // matches provider — required by iter63 guard
                duration_seconds: Number(soraDuration) || 4,
                size: soraSize,
                video_prompt_override: prompt,
                // iter66.2 — BE requires `confirmed=true` for paid Sora.
                // Premium retry only runs after the dedicated sora-2-pro
                // VideoCostModal confirms, so this is safe to send.
                confirmed: true,
            });
            toast.success("Premium Sora-2-Pro retry queued.");
            setSoraProOpen(false);
            onChange?.();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail?.error === "duplicate_active_paid_job_for_shot") {
                toast.error(detail?.hint || "Another Sora job is already running for this shot.");
            } else if (detail?.error === "daily_cost_cap_reached") {
                toast.error(
                    `Daily Sora cost cap reached: $${Number(detail.used_usd || 0).toFixed(2)} / `
                    + `$${Number(detail.cap_usd || 0).toFixed(2)}.`,
                );
            } else if (detail?.error === "model_not_enabled") {
                toast.error("Sora-2-Pro is not enabled on this server.");
            } else if (detail?.error === "cost_modal_confirmation_required") {
                toast.error(
                    "Cost modal confirmation missing. Reopen the cost "
                    + "modal and click Confirm to send confirmed=true.",
                );
            } else {
                toast.error(err?.userMessage || "Premium retry failed");
            }
            setSoraProOpen(false);
        } finally { setBusy(false); }
    };

    const approve = async (jobId) => {
        try {
            const { data } = await api.post(
                `/projects/${project.id}/video-jobs/${jobId}/approve`);
            toast.success(`QC request ${data?.id || "created"}.`);
            onChange?.();
        } catch (err) {
            toast.error(err?.userMessage || "Approve failed");
        }
    };

    // iter65 — Regenerate the most-recent completed video for this shot
    // with a director note. ALWAYS posts confirmed=true (the modal's
    // confirm button is the cost-modal confirmation). Caps + duplicate
    // guard + uniqueness gate still apply on the backend.
    const submitRegen = async (note, refs) => {
        const target = succeeded || active;
        if (!target) {
            toast.error("Generate a v1 clip first before regenerating.");
            return;
        }
        setRegenBusy(true);
        try {
            await api.post(
                `/projects/${project.id}/video-jobs/${target.id}/regenerate-with-notes`,
                {
                    raw_director_note: note,
                    uploaded_reference_asset_ids: refs || [],
                    confirmed: true,
                    allow_duplicate: false,
                },
            );
            toast.success("New version queued. Compare versions when ready.");
            setRegenOpen(false);
            onChange?.();
        } catch (err) {
            const d = err?.response?.data?.detail;
            if (d?.error === "duplicate_active_paid_job_for_shot") {
                toast.error("A paid Sora job for this shot is still running. Wait or cancel it before regenerating.");
            } else if (d?.error === "shot_uniqueness_blocked") {
                toast.error("This shot is flagged as a duplicate. Differentiate it first or set allow_duplicate.");
            } else if (d?.error === "daily_cost_cap_reached") {
                toast.error(`Daily Sora cost cap reached: $${Number(d.used_usd || 0).toFixed(2)} / $${Number(d.cap_usd || 0).toFixed(2)}.`);
            } else if (d?.error === "daily_cap_reached") {
                toast.error(`Daily cap reached (${d.cap}). Try again tomorrow.`);
            } else if (d?.error === "ffmpeg_unavailable") {
                toast.error("FFmpeg missing on backend. Regen blocked.");
            } else if (d?.error === "raw_director_note_required") {
                toast.error("Please type a director note before regenerating.");
            } else if (d?.error === "cost_modal_confirmation_required") {
                toast.error("Cost modal not confirmed. Reopen the modal and re-confirm.");
            } else {
                toast.error(err?.userMessage || "Regenerate failed");
            }
        } finally {
            setRegenBusy(false);
        }
    };

    return (
        <div
            data-testid={`video-shot-card-${shot.shot_id}`}
            className="card-base p-4 space-y-3"
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">Shot</span>
                    <span className="text-sm font-mono text-zinc-100">{shot.shot_id}</span>
                </div>
                <StatusChip job={active || succeeded || null} />
            </div>

            {editing ? (
                <textarea
                    data-testid={`video-shot-prompt-${shot.shot_id}`}
                    rows={3}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    className="w-full bg-black border border-zinc-800 rounded-sm px-2 py-1.5 text-sm"
                />
            ) : (
                <p className="text-sm text-zinc-300 leading-snug whitespace-pre-wrap">
                    {prompt || <span className="text-zinc-600">(No video_prompt yet — run Prompt Library first.)</span>}
                </p>
            )}

            <div className="flex items-center justify-between text-[10px] text-zinc-500">
                <button
                    data-testid={`video-shot-edit-${shot.shot_id}`}
                    onClick={() => setEditing(v => !v)}
                    className="underline underline-offset-2 hover:text-zinc-200"
                >
                    {editing ? "Done" : "Edit before submit"}
                </button>
                <span data-testid={`video-shot-source-label-${shot.shot_id}`}>
                    {(() => {
                        const sizeLabel = (
                            (soraCaps?.size_labels || {})[soraSize] || soraSize
                        );
                        const source = (
                            shot?._source === "sora_pack" ? "from Sora prompt pack"
                            : shot?._source === "storyboard" ? "from Storyboard fallback"
                            : shot?._source === "prompts" ? "from Prompt Library"
                            : shot?._source === "jobs_only" ? "from jobs only"
                            : "manual import"
                        );
                        return soraEnabled
                            ? `${soraDuration}s · ${sizeLabel} · ${source}`
                            : `${source}`;
                    })()}
                </span>
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-zinc-900">
                <input
                    type="file"
                    accept="video/mp4,video/quicktime,video/webm"
                    ref={fileInputRef}
                    onChange={onFileSelected}
                    className="hidden"
                    data-testid={`video-shot-file-input-${shot.shot_id}`}
                />
                <button
                    data-testid={`video-shot-upload-${shot.shot_id}`}
                    onClick={pickFile}
                    disabled={busy || !!active}
                    className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-50"
                >
                    <Upload className="w-3.5 h-3.5" />
                    Upload from Google Flow
                </button>
                <button
                    data-testid={`video-shot-generate-${shot.shot_id}`}
                    disabled={!soraEnabled || !ffmpegReady || busy || !!active || !prompt.trim()}
                    onClick={() => soraEnabled && ffmpegReady && setSoraOpen(true)}
                    title={
                        !soraEnabled
                            ? "Direct generation available only when admin enables Sora (VIDEO_STUDIO_PROVIDERS=manual_import,sora-2)."
                            : !ffmpegReady
                                ? "FFmpeg not installed on backend — Sora outputs cannot be re-muxed for browser playback."
                                : !prompt.trim()
                                    ? "Add a video_prompt first."
                                    : !!active
                                        ? "A paid Sora job for this shot is already running. Cancel it before submitting another."
                                        : `Generate this shot with Sora-2 at ${soraDuration}s ${soraSize}.`
                    }
                    className={
                        (soraEnabled && ffmpegReady)
                            ? "btn-primary text-xs px-3 py-1.5 disabled:opacity-40"
                            : "text-xs px-3 py-1.5 text-zinc-500 border border-dashed border-zinc-800 rounded-sm cursor-not-allowed"
                    }
                >
                    {(soraEnabled && ffmpegReady) ? <Play className="w-3.5 h-3.5" /> : null}
                    {soraEnabled ? "Generate (sora-2)" : "Generate (admin only)"}
                </button>
                {/* iter63 — Premium Sora-2-Pro retry. Surfaces ONLY when the
                    admin has explicitly whitelisted sora-2-pro AND the standard
                    Sora-2 path failed for this shot. Never auto-triggered. */}
                {soraProEnabled && (
                    <button
                        data-testid={`video-shot-generate-pro-${shot.shot_id}`}
                        disabled={!ffmpegReady || busy || !!active || !prompt.trim()}
                        onClick={() => setSoraProOpen(true)}
                        title={
                            !ffmpegReady
                                ? "FFmpeg not installed on backend."
                                : !prompt.trim()
                                    ? "Add a video_prompt first."
                                    : !!active
                                        ? "A paid Sora job for this shot is already running."
                                        : "Premium manual retry with Sora-2-Pro. Higher quality, higher cost. Cost modal will confirm before generation."
                        }
                        className="text-xs px-3 py-1.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-100 hover:bg-amber-500/15 disabled:opacity-40 inline-flex items-center gap-1.5"
                    >
                        <Play className="w-3.5 h-3.5" />
                        Retry with Sora-2-Pro (premium)
                    </button>
                )}
                {/* Sprint F2 — fal.ai / Kling v2.5 Turbo Pro image-to-video.
                    Two-signal gate: backend must register `fal-kling` AND
                    preview must set REACT_APP_ENABLE_FAL_KLING=true. */}
                {falKlingEnabled && (
                    <button
                        data-testid={`video-shot-generate-fal-kling-${shot.shot_id}`}
                        disabled={busy || !!active || !prompt.trim()}
                        onClick={() => setFalKlingOpen(true)}
                        title={
                            !prompt.trim()
                                ? "Add a video_prompt first — motion-first envelope needs a base beat."
                                : !!active
                                    ? "A paid job for this shot is already running."
                                    : `Generate this shot with Kling v2.5 Turbo Pro (image-to-video). Requires an authorized reference image on the shot. ${falKlingDuration}s clip.`
                        }
                        className="text-xs px-3 py-1.5 rounded-sm border border-sky-500/40 bg-sky-500/10 text-sky-100 hover:bg-sky-500/15 disabled:opacity-40 inline-flex items-center gap-1.5"
                    >
                        <Sparkles className="w-3.5 h-3.5" />
                        Generate (fal-kling · image-to-video)
                    </button>
                )}
            </div>

            {soraEnabled && (
                <div
                    data-testid={`video-shot-sora-controls-${shot.shot_id}`}
                    className="grid grid-cols-3 gap-2 pt-2 border-t border-zinc-900"
                >
                    <label className="text-[10px] text-zinc-500 flex flex-col gap-1">
                        Model
                        <select
                            data-testid={`video-shot-model-${shot.shot_id}`}
                            value="sora-2"
                            disabled
                            title="Standard generation always uses sora-2. Premium retry uses the dedicated button."
                            className="bg-black border border-zinc-800 rounded-sm px-2 py-1 text-xs text-zinc-200 disabled:opacity-70"
                        >
                            <option value="sora-2">sora-2</option>
                        </select>
                    </label>
                    <label className="text-[10px] text-zinc-500 flex flex-col gap-1">
                        Size
                        <select
                            data-testid={`video-shot-size-${shot.shot_id}`}
                            value={soraSize}
                            onChange={(e) => { setUserTouchedSoraControls(true); setSoraSize(e.target.value); }}
                            className="bg-black border border-zinc-800 rounded-sm px-2 py-1 text-xs text-zinc-200"
                        >
                            {(soraCaps?.sizes || ["1280x720"]).map(s => (
                                <option key={s} value={s}>
                                    {(soraCaps?.size_labels || {})[s]
                                        ? `${s} — ${(soraCaps.size_labels)[s]}`
                                        : s}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="text-[10px] text-zinc-500 flex flex-col gap-1">
                        Duration
                        <select
                            data-testid={`video-shot-duration-${shot.shot_id}`}
                            value={soraDuration}
                            onChange={(e) => { setUserTouchedSoraControls(true); setSoraDuration(Number(e.target.value)); }}
                            className="bg-black border border-zinc-800 rounded-sm px-2 py-1 text-xs text-zinc-200"
                        >
                            {(soraCaps?.durations || [4]).map(d => (
                                <option key={d} value={d}>{d}s</option>
                            ))}
                        </select>
                    </label>
                </div>
            )}

            {/* Latest succeeded preview */}
            {succeeded && (() => {
                // iter60.3 — Native <video> tags do not send the Authorization
                // header. The stream endpoint accepts `?auth=<jwt>` as a
                // fallback so the player can fetch the bytes. We also expose
                // an "Open in new tab" fallback in case inline playback is
                // blocked (Safari/iOS sometimes refuses inline mp4 over Range).
                const token = (typeof window !== "undefined")
                    ? (localStorage.getItem("aivos_token") || "")
                    : "";
                const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
                const streamUrl =
                    `${base}/api/assets/${succeeded.output_asset_id}/stream`
                    + (token ? `?auth=${encodeURIComponent(token)}` : "");
                return (
                    <div className="pt-3 border-t border-zinc-900 space-y-2"
                         data-testid={`video-shot-preview-${shot.shot_id}`}>
                        {/* iter66 — Diff preview chip + fallback pill.
                            The chip shows succeeded.regen_envelope (set by
                            iter65 when this job is a regenerated version).
                            The pill surfaces _director_source so operator
                            knows when the Prompt Director fell back. */}
                        {(succeeded.version_number > 1 || succeeded.regen_envelope) && (
                            <div className="flex flex-wrap items-center gap-2 text-[11px]">
                                <span
                                    data-testid={`video-shot-version-chip-${shot.shot_id}`}
                                    className="inline-flex items-center gap-1 rounded-sm border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 font-mono uppercase tracking-wider text-amber-200"
                                >
                                    v{succeeded.version_number || "?"}
                                    {succeeded.regen_envelope?.edit_intent
                                        ? ` · ${succeeded.regen_envelope.edit_intent.replace(/_/g, " ")}`
                                        : ""}
                                </span>
                                {(succeeded.regen_envelope?.change_list || []).slice(0, 2).map((c, i) => (
                                    <span
                                        key={i}
                                        data-testid={`video-shot-diff-chip-${shot.shot_id}-${i}`}
                                        className="inline-flex items-center rounded-sm border border-zinc-800 bg-zinc-950/60 px-1.5 py-0.5 text-[10.5px] text-zinc-300 max-w-xs truncate"
                                        title={c}
                                    >
                                        {c}
                                    </span>
                                ))}
                                {succeeded.regen_envelope?.post_production_required?.required && (
                                    <span className="inline-flex items-center rounded-sm border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10.5px] text-amber-200">
                                        post-prod needed
                                    </span>
                                )}
                                {(succeeded.regen_envelope?._director_source || "").startsWith("fallback") && (
                                    <span
                                        data-testid={`video-shot-fallback-pill-${shot.shot_id}`}
                                        className="inline-flex items-center rounded-sm border border-zinc-700 bg-zinc-900 px-1.5 py-0.5 text-[10.5px] text-zinc-400"
                                        title="Prompt Director LLM was unavailable; deterministic fallback was used."
                                    >
                                        fallback prompt director
                                    </span>
                                )}
                            </div>
                        )}
                        {qcChip && (
                            <div
                                data-testid={`video-shot-qc-chip-row-${shot.shot_id}`}
                                className="flex flex-wrap items-center gap-2 text-[11px]"
                            >
                                <span
                                    data-testid={`video-shot-qc-chip-${shot.shot_id}`}
                                    className="inline-flex items-center gap-1 rounded-sm border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 font-mono uppercase tracking-wider text-emerald-200"
                                    title={
                                        qcChip.source_video_job_id
                                            ? `QC review now points at v${qcChip.version} (job ${qcChip.source_video_job_id.slice(0, 8)}…)`
                                            : `QC review now points at v${qcChip.version}`
                                    }
                                >
                                    <CheckCircle2 className="w-3 h-3" />
                                    QC → v{qcChip.version}
                                </span>
                                {qcChip.asset_id && (
                                    <span className="text-[10.5px] text-emerald-300/70 font-mono">
                                        asset {String(qcChip.asset_id).slice(0, 8)}…
                                    </span>
                                )}
                            </div>
                        )}
                        <video
                            data-testid={`video-shot-player-${shot.shot_id}`}
                            controls
                            preload="metadata"
                            playsInline
                            className="w-full rounded-sm border border-zinc-800 bg-black"
                            src={streamUrl}
                        />
                        <div className="flex items-center justify-between text-[11px] text-zinc-500">
                            <span>
                                {(succeeded.duration_seconds || 4)}s · {(succeeded.size || "1280x720")}
                                {succeeded.actual_cost
                                    ? ` · $${Number(succeeded.actual_cost).toFixed(2)}`
                                    : ""}
                            </span>
                            <div className="flex items-center gap-3">
                                <a
                                    data-testid={`video-shot-open-${shot.shot_id}`}
                                    href={streamUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline underline-offset-2 hover:text-zinc-200 inline-flex items-center gap-1"
                                >
                                    <ExternalLink className="w-3 h-3" /> Open in new tab
                                </a>
                                <a
                                    data-testid={`video-shot-download-${shot.shot_id}`}
                                    href={streamUrl}
                                    download={`${shot.shot_id}-${succeeded.duration_seconds || 4}s.mp4`}
                                    className="underline underline-offset-2 hover:text-zinc-200 inline-flex items-center gap-1"
                                >
                                    <Download className="w-3 h-3" /> Download
                                </a>
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-2">
                            <button
                                data-testid={`video-shot-version-compare-${shot.shot_id}`}
                                onClick={() => setCompareOpen(true)}
                                className="text-xs px-3 py-1.5 text-zinc-300 hover:text-zinc-100 border border-zinc-800 rounded-sm inline-flex items-center gap-1.5"
                                title="See all versions of this shot and pick the one to send to QC."
                            >
                                <History className="w-3.5 h-3.5" />
                                Compare versions
                            </button>
                            {soraEnabled && (
                                <button
                                    data-testid={`video-shot-regen-${shot.shot_id}`}
                                    onClick={() => setRegenOpen(true)}
                                    disabled={!!active || !ffmpegReady}
                                    className="text-xs px-3 py-1.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-100 hover:bg-amber-500/15 disabled:opacity-40 inline-flex items-center gap-1.5"
                                    title="Type a director note and regenerate this shot as a new version. Original is preserved."
                                >
                                    <Sparkles className="w-3.5 h-3.5" />
                                    Regenerate with Director Input
                                </button>
                            )}
                            <button
                                data-testid={`video-shot-approve-${shot.shot_id}`}
                                onClick={() => approve(succeeded.id)}
                                className="btn-primary text-xs px-3 py-1.5"
                            >
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Approve & send to QC
                            </button>
                        </div>
                    </div>
                );
            })()}

            {/* Active job actions */}
            {active && (
                <div className="text-[11px] text-zinc-400 flex items-center justify-between">
                    <span><Loader2 className="inline w-3 h-3 mr-1 animate-spin" /> {active.status}</span>
                    <button
                        data-testid={`video-shot-cancel-${shot.shot_id}`}
                        onClick={() => cancel(active.id)}
                        className="text-zinc-500 hover:text-rose-400"
                    >Cancel</button>
                </div>
            )}

            <VideoCostModal
                open={costOpen}
                onClose={() => { setCostOpen(false); setPendingFile(null); setPendingAssetId(""); }}
                onConfirm={submitManualImport}
                provider="manual_import"
                costUsd={0}
                currency="USD"
                durationSeconds={4}
                size="1280x720"
                shotId={shot.shot_id}
                busy={busy}
            />

            {soraEnabled && (
                <VideoCostModal
                    open={soraOpen}
                    onClose={() => setSoraOpen(false)}
                    onConfirm={submitSora}
                    provider="sora-2"
                    costUsd={Number(
                        (soraStdCaps?.est_usd_per_clip_by_duration || {})[String(soraDuration)] || 0
                    )}
                    currency="USD"
                    durationSeconds={Number(soraDuration)}
                    size={soraSize}
                    shotId={shot.shot_id}
                    busy={busy}
                />
            )}
            {/* iter63 — Premium Sora-2-Pro cost modal. Separate from the
                standard modal so the operator gets a distinct premium-cost
                confirmation. Only rendered when sora-2-pro is whitelisted. */}
            {soraProEnabled && (
                <VideoCostModal
                    data-testid={`video-shot-pro-cost-modal-${shot.shot_id}`}
                    open={soraProOpen}
                    onClose={() => setSoraProOpen(false)}
                    onConfirm={submitSoraPro}
                    provider="sora-2-pro"
                    costUsd={Number(
                        (soraProCaps?.est_usd_per_clip_by_duration || {})[String(soraDuration)] || 0
                    )}
                    currency="USD"
                    durationSeconds={Number(soraDuration)}
                    size={soraSize}
                    shotId={shot.shot_id}
                    busy={busy}
                />
            )}
            {/* Sprint F2 — fal.ai / Kling image-to-video cost modal.
                Reuses the standard VideoCostModal contract so the paid-cost
                confirmation UX is uniform across providers. */}
            {falKlingEnabled && (
                <VideoCostModal
                    data-testid={`video-shot-fal-kling-cost-modal-${shot.shot_id}`}
                    open={falKlingOpen}
                    onClose={() => setFalKlingOpen(false)}
                    onConfirm={submitFalKling}
                    provider="fal-kling"
                    costUsd={falKlingCost}
                    currency="USD"
                    durationSeconds={Number(falKlingDuration)}
                    size={falKlingCaps?.default_size || "1280x720"}
                    shotId={shot.shot_id}
                    busy={busy}
                />
            )}
            {pendingFile && (
                <div className="text-[10px] text-zinc-600">
                    Selected: {pendingFile.name}
                </div>
            )}

            {/* iter65 — Director regeneration + version compare modals */}
            <RegenerateWithNotesModal
                open={regenOpen}
                target="video"
                shotId={shot.shot_id}
                costPreview={
                    (soraStdCaps?.est_usd_per_clip_by_duration || {})[String(soraDuration)]
                        ? `$${Number((soraStdCaps.est_usd_per_clip_by_duration || {})[String(soraDuration)]).toFixed(2)} (${soraDuration}s sora-2)`
                        : `~$${(soraDuration === 12 ? 1.20 : soraDuration === 8 ? 0.80 : 0.40).toFixed(2)} (${soraDuration}s sora-2)`
                }
                busy={regenBusy}
                onClose={() => setRegenOpen(false)}
                onConfirm={submitRegen}
            />
            <VersionCompareModal
                open={compareOpen}
                api={api}
                projectId={project.id}
                target="video"
                shotId={shot.shot_id}
                onApproved={(vn, qcReview) => {
                    // iter66.1 — surface the live QC routing payload as
                    // an inline chip on the shot card. Falls back to a
                    // version-only chip when the BE doesn't return a
                    // qc_review (e.g. v1 approval without a parent job).
                    setQcChip({
                        version: qcReview?.selected_version_number ?? vn,
                        asset_id: qcReview?.asset_id || "",
                        source_video_job_id: qcReview?.source_video_job_id || "",
                    });
                    onChange?.();
                }}
                onClose={() => setCompareOpen(false)}
            />
        </div>
    );
}

function StatusChip({ job }) {
    if (!job) {
        return <span className="text-[10px] text-zinc-600 uppercase tracking-wider">No job</span>;
    }
    const map = {
        queued:     { color: "text-zinc-400 border-zinc-700", icon: Clock, label: "Queued" },
        submitted:  { color: "text-sky-300 border-sky-500/40", icon: Loader2, label: "Submitted" },
        processing: { color: "text-sky-300 border-sky-500/40", icon: Loader2, label: "Processing" },
        succeeded:  { color: "text-emerald-300 border-emerald-500/40", icon: CheckCircle2, label: "Succeeded" },
        failed:     { color: "text-rose-300 border-rose-500/40", icon: AlertTriangle, label: "Failed" },
        cancelled:  { color: "text-zinc-400 border-zinc-700", icon: XCircle, label: "Cancelled" },
        expired:    { color: "text-zinc-400 border-zinc-700", icon: XCircle, label: "Expired" },
    };
    const s = map[job.status] || map.queued;
    const Icon = s.icon;
    return (
        <span className={`text-[10px] inline-flex items-center gap-1 px-2 py-0.5 border rounded-sm uppercase tracking-wider ${s.color}`}>
            <Icon className={`w-3 h-3 ${["submitted","processing"].includes(job.status) ? "animate-spin" : ""}`} />
            <span>{s.label}</span>
        </span>
    );
}
